import { Utils } from "../Utils";

/**
 * The strings service. Used for loading localized strings
 */
export interface IStringsService {
  /**
   * Loads required all string packages asynchronously
   *
   * @param language - The language for which the strings should be loaded
   */
  loadStringsAsync(language: string): Promise<void>;
}

/**
 * The strings service. Used for loading localized strings
 */
export class StringsService implements IStringsService {
  /**
   * The list of all the required string packages
   */
  private requiredStringPackages = ["client", "website"];

  /**
   * Initializes a new instance of the StringsService class.
   *
   * @param version - The version of the application
   * @param apiVersion - The version of the API
   * @param createScriptURL - factory function for taking a script URL and making a trusted script URL from it
   */
  public constructor(private version: string, private apiVersion: string, private createScriptURL: (src: string) => TrustedScriptURL) {}

  /**
   * Loads required all string packages asynchronously
   *
   * @param language - The language for which the strings should be loaded
   */
  public async loadStringsAsync(language: string): Promise<void> {
    const tasks: Promise<void>[] = [];
    for (const pkgId of this.requiredStringPackages) {
      tasks.push(this.loadStringPackageAsync(pkgId, language));
    }

    await Promise.all(tasks);
  }

  /**
   * Loads a single string package asynchronously
   *
   * @param pkgId - The package id
   * @param language - The language
   */
  private async loadStringPackageAsync(pkgId: string, language: string): Promise<void> {
    const elementId = `m365rca-${pkgId}strings`;
    const existing = document.querySelector<HTMLScriptElement>(`script#${elementId}`);

    const scriptSrc = `/api/${this.apiVersion}/strings/${language}/${pkgId}?v=${this.version}`;
    if (existing) {
      existing.parentElement?.removeChild(existing);
    }

    await Utils.loadScriptAsync(scriptSrc, elementId, this.createScriptURL);
  }
}
